/**
 * Off-site destinations that appear in more than one place.
 *
 * The LinkedIn URL lived as three separate string literals — both footers and
 * the CEO page — and predictably drifted: the CEO page was updated to the
 * current company page on 2026-08-26 while the footers kept pointing at the
 * older `ca.linkedin.com/company/ai2aim`, so the site advertised two different
 * LinkedIn destinations at once. Same failure mode `src/config/nav.ts` was
 * created to end, so it gets the same remedy: one definition, every consumer
 * imports it.
 *
 * Anything here is public and outbound. Add a destination only when a second
 * surface needs it; a URL used in exactly one place belongs in that file.
 */
export const SITE = {
  /** Company page. Confirmed by the user on 2026-08-26. */
  linkedin: "https://www.linkedin.com/company/ai2aim-inc",
  facebook: "https://www.facebook.com/profile.php?id=61592720457932",
  instagram: "https://www.instagram.com/ai2aim.ai/",
  /**
   * Not registered yet. Confirmed by the user on 2026-08-28 that these accounts
   * are coming later.
   *
   * `null` rather than "#" or an empty string on purpose: consumers must decide
   * what to do with a destination that does not exist, and the type forces
   * them to. A placeholder href would render a link that looks live and goes
   * nowhere, which is worse than showing nothing.
   */
  tiktok: null,
  x: null,
} as const;

export type SocialKey = "linkedin" | "facebook" | "instagram" | "tiktok" | "x";

export interface SocialLink {
  key: SocialKey;
  label: string;
  /** null while the account does not exist yet. */
  href: string | null;
}

/** Display order for the social row. Live accounts first. */
export const SOCIALS: SocialLink[] = [
  { key: "linkedin", label: "LinkedIn", href: SITE.linkedin },
  { key: "facebook", label: "Facebook", href: SITE.facebook },
  { key: "instagram", label: "Instagram", href: SITE.instagram },
  { key: "tiktok", label: "TikTok", href: SITE.tiktok },
  { key: "x", label: "X", href: SITE.x },
];
